import React, { useState, useEffect } from 'react';
import api from './api';
import { extractBarkToken } from './bark';
import { useToast } from './Toast';
import './Settings.css';

const PUSH_TYPE_LABELS = {
  bark: 'Bark',
  wxPlugin: '微信插件',
  wechat: '企微',
  wechatWork: '企微',
  wechat_work: '企微',
  qyWechat: '企微',
  qy_wechat: '企微'
};

const JUMP_TARGET_OPTIONS = [
  { key: 'default', label: '默认' },
  { key: 'official', label: '官网' },
  { key: 'letao', label: '乐淘一番' },
  { key: 'rakutao', label: '乐酷淘' },
  { key: 'wameiji', label: '挖煤姬' }
];

const SWITCHABLE_PUSH_TYPES = [
  { key: 'bark', label: 'Bark' },
  { key: 'wechat', label: '企微' }
];

const WECHAT_STYLE_OPTIONS = [
  { key: 'card_plus', label: '卡片样式' },
  { key: 'split', label: '分离样式' }
];

const WECHAT_STYLE_DETAILS = {
  card_plus: {
    label: '卡片样式',
    description: '点击卡片跳转至一键复制或一键跳转的界面，适合想直接跳转对应代购平台购买的用户。',
    samples: [
      'https://xin-feng.xyz/user-system-assets/wechat_style_card_1.jpg',
      'https://xin-feng.xyz/user-system-assets/wechat_style_card_2.jpg',
      'https://xin-feng.xyz/user-system-assets/wechat_style_card_3.jpg'
    ]
  },
  split: {
    label: '分离样式',
    description: '商品描述和图片分开推送，适合想直接复制煤炉链接的用户。',
    samples: [
      'https://xin-feng.xyz/user-system-assets/wechat_style_split.jpg'
    ]
  }
};

const DEFAULT_JUMP_TARGET = 'default';
const DEFAULT_WECHAT_STYLE = 'card_plus';
const JUMP_TARGET_KEYS = new Set(JUMP_TARGET_OPTIONS.map((option) => option.key));
const WECHAT_STYLE_KEYS = new Set(WECHAT_STYLE_OPTIONS.map((option) => option.key));
const WECHAT_PUSH_TYPES = new Set(['wechat', 'wechatWork', 'wechat_work', 'qyWechat', 'qy_wechat']);
const LEGACY_JUMP_TARGET_ALIASES = {
  mercari: 'official'
};

const formatPushType = (pushType) => {
  const normalizedPushType = (pushType || '').trim();

  if (!normalizedPushType) {
    return '未设置';
  }

  return PUSH_TYPE_LABELS[normalizedPushType] || normalizedPushType;
};

const normalizeJumpTarget = (jumpTarget) => {
  const rawJumpTarget = (jumpTarget || '').trim();
  const normalizedJumpTarget = LEGACY_JUMP_TARGET_ALIASES[rawJumpTarget] || rawJumpTarget;
  return JUMP_TARGET_KEYS.has(normalizedJumpTarget) ? normalizedJumpTarget : DEFAULT_JUMP_TARGET;
};

const serializeJumpTarget = (jumpTarget) => (
  normalizeJumpTarget(jumpTarget) === DEFAULT_JUMP_TARGET ? null : normalizeJumpTarget(jumpTarget)
);

const normalizeWechatStyle = (wechatStyle) => {
  const normalizedWechatStyle = (wechatStyle || '').trim();
  return WECHAT_STYLE_KEYS.has(normalizedWechatStyle) ? normalizedWechatStyle : DEFAULT_WECHAT_STYLE;
};

const normalizeSwitchablePushType = (value) => {
  const normalizedValue = (value || '').trim();

  if (normalizedValue === 'bark') {
    return 'bark';
  }

  if (WECHAT_PUSH_TYPES.has(normalizedValue)) {
    return 'wechat';
  }

  return '';
};

function PushTypeSwitch({ value, options, onChange, disabled = false, ariaLabel = '推送方式' }) {
  return (
    <div className="settings-push-type-switch" role="group" aria-label={ariaLabel}>
      {options.map((option) => {
        const isActive = option.key === value;

        return (
          <button
            key={option.key}
            type="button"
            className={`settings-push-type-button${isActive ? ' active' : ''}`}
            aria-pressed={isActive}
            disabled={disabled}
            onClick={() => onChange(option.key)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function AutoSaveStatus({ isSaving }) {
  return (
    <span
      className={`settings-inline-status${isSaving ? ' settings-inline-status--visible' : ' settings-inline-status--hidden'}`}
      aria-hidden={isSaving ? 'false' : 'true'}
      aria-live="polite"
    >
      保存中...
    </span>
  );
}

function SettingsModal({ title, titleId, className = '', bodyClassName = '', footer, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal-content${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id={titleId}>{title}</h3>
          <button className="modal-close" aria-label="关闭弹窗" onClick={onClose}>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className={`modal-body${bodyClassName ? ` ${bodyClassName}` : ''}`}>
          {children}
        </div>
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function JumpTargetDropdown({ value, options, onChange, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find((option) => option.key === value) || options[0];

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div className="category-select-field settings-jump-target-field" onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setIsOpen(false);
      }
    }}>
      <button
        type="button"
        className={`category-select-trigger${isOpen ? ' open' : ''}`}
        aria-label={`跳转目标：${selectedOption.label}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={() => setIsOpen(open => !open)}
      >
        <span className="category-select-value">
          {selectedOption.label}
        </span>
        <span className="category-select-chevron" aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="category-select-menu" role="listbox" aria-label="跳转目标">
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              role="option"
              aria-selected={option.key === value}
              className={`category-select-option${option.key === value ? ' selected' : ''}`}
              onClick={() => handleSelect(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Settings({ onLogout }) {
  const { addToast } = useToast();
  const [userEmail, setUserEmail] = useState('');
  const [barkToken, setBarkToken] = useState('');
  const [wechatReceiver, setWechatReceiver] = useState('');
  const [pushType, setPushType] = useState('');
  const [isPushTypeSaving, setIsPushTypeSaving] = useState(false);
  const [wechatStyle, setWechatStyle] = useState(DEFAULT_WECHAT_STYLE);
  const [isWechatStyleSaving, setIsWechatStyleSaving] = useState(false);
  const [jumpTarget, setJumpTarget] = useState(DEFAULT_JUMP_TARGET);
  const [savedJumpTarget, setSavedJumpTarget] = useState(DEFAULT_JUMP_TARGET);
  const [isJumpTargetSaving, setIsJumpTargetSaving] = useState(false);
  const [exchangeRate, setExchangeRate] = useState('');
  const [defaultExchangeRate, setDefaultExchangeRate] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExchangeRateModalOpen, setIsExchangeRateModalOpen] = useState(false);
  const [isWechatStyleSampleOpen, setIsWechatStyleSampleOpen] = useState(false);
  const [wechatStyleSampleImageStatus, setWechatStyleSampleImageStatus] = useState({});
  const [tempBarkToken, setTempBarkToken] = useState('');
  const [tempExchangeRate, setTempExchangeRate] = useState('');

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await api.get('/api/user-info');
        setUserEmail(response.data.email);
        setBarkToken(response.data.barkToken || '');
        setWechatReceiver((response.data.wechatReceiver || '').trim());
        setPushType((response.data.pushType || '').trim());
        setWechatStyle(normalizeWechatStyle(response.data.wechatStyle));
        const normalizedJumpTarget = normalizeJumpTarget(response.data.jumpTarget);
        setJumpTarget(normalizedJumpTarget);
        setSavedJumpTarget(normalizedJumpTarget);
        setExchangeRate((response.data.exchangeRate || '').toString().trim());
        setDefaultExchangeRate((response.data.defaultExchangeRate || '').toString().trim());
      } catch (error) {
      }
    };
    fetchUserInfo();
  }, []);

  const isAnyModalOpen = isModalOpen || isExchangeRateModalOpen || isWechatStyleSampleOpen;

  useEffect(() => {
    if (!isAnyModalOpen) {
      return undefined;
    }

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalWidth = document.body.style.width;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = originalWidth;

      if (scrollY > 0 && typeof window.scrollTo === 'function') {
        try {
          window.scrollTo(0, scrollY);
        } catch (error) {
        }
      }
    };
  }, [isAnyModalOpen]);

  const handleEditClick = () => {
    setTempBarkToken(barkToken);
    setIsModalOpen(true);
  };

  const handleSaveBarkToken = async () => {
    try {
      const processedToken = extractBarkToken(tempBarkToken);
      await api.post('/api/update-bark-token', { barkToken: processedToken });
      setBarkToken(processedToken);
      setIsModalOpen(false);
    } catch (error) {
    }
  };

  const handleEditExchangeRateClick = () => {
    setTempExchangeRate(exchangeRate);
    setIsExchangeRateModalOpen(true);
  };

  const handleOpenWechatStyleSample = () => {
    setWechatStyleSampleImageStatus({});
    setIsWechatStyleSampleOpen(true);
  };

  const updateWechatStyleSampleImageStatus = (sampleUrl, status) => {
    setWechatStyleSampleImageStatus((currentStatus) => ({
      ...currentStatus,
      [sampleUrl]: status
    }));
  };

  const handleSaveExchangeRate = async () => {
    try {
      const normalizedExchangeRate = tempExchangeRate.trim();
      const response = await api.post('/api/update-exchange-rate', { exchangeRate: normalizedExchangeRate });
      setExchangeRate((response.data?.exchangeRate ?? normalizedExchangeRate).toString());
      setIsExchangeRateModalOpen(false);
    } catch (error) {
    }
  };

  const handlePushTypeChange = async (nextPushType) => {
    const normalizedPushType = normalizeSwitchablePushType(nextPushType);

    if (!normalizedPushType || normalizedPushType === normalizeSwitchablePushType(pushType)) {
      return;
    }

    const previousPushType = pushType;
    setPushType(normalizedPushType);
    setIsPushTypeSaving(true);

    try {
      const response = await api.post('/api/update-push-type', { pushType: normalizedPushType });
      const savedPushType = normalizeSwitchablePushType(response.data?.pushType || normalizedPushType);
      setPushType(savedPushType || normalizedPushType);
      addToast('推送方式已保存', 'success');
    } catch (error) {
      setPushType(previousPushType);
      addToast('推送方式保存失败，请稍后重试', 'error');
    } finally {
      setIsPushTypeSaving(false);
    }
  };

  const handleJumpTargetChange = async (nextJumpTarget) => {
    const normalizedJumpTarget = normalizeJumpTarget(nextJumpTarget);

    if (normalizedJumpTarget === jumpTarget) {
      return;
    }

    const previousJumpTarget = savedJumpTarget;
    setJumpTarget(normalizedJumpTarget);
    setIsJumpTargetSaving(true);

    try {
      const response = await api.post('/api/update-jump-target', {
        jumpTarget: serializeJumpTarget(normalizedJumpTarget)
      });
      const savedTarget = normalizeJumpTarget(response.data?.jumpTarget || normalizedJumpTarget);
      setJumpTarget(savedTarget);
      setSavedJumpTarget(savedTarget);
      addToast('跳转目标已保存', 'success');
    } catch (error) {
      setJumpTarget(previousJumpTarget);
      addToast('跳转目标保存失败，请稍后重试', 'error');
    } finally {
      setIsJumpTargetSaving(false);
    }
  };

  const handleWechatStyleChange = async (nextWechatStyle) => {
    const normalizedWechatStyle = normalizeWechatStyle(nextWechatStyle);

    if (normalizedWechatStyle === wechatStyle) {
      return;
    }

    const previousWechatStyle = wechatStyle;
    setWechatStyle(normalizedWechatStyle);
    setIsWechatStyleSaving(true);

    try {
      const response = await api.post('/api/update-wechat-style', {
        wechatStyle: normalizedWechatStyle
      });
      setWechatStyle(normalizeWechatStyle(response.data?.wechatStyle || normalizedWechatStyle));
      addToast('推送样式已保存', 'success');
    } catch (error) {
      setWechatStyle(previousWechatStyle);
      addToast('推送样式保存失败，请稍后重试', 'error');
    } finally {
      setIsWechatStyleSaving(false);
    }
  };

  const handleLogoutClick = () => {
    if (window.confirm('确定要退出登录吗？')) {
      onLogout();
    }
  };

  const isUsingDefaultExchangeRate = !exchangeRate && !!defaultExchangeRate;
  const hasDisplayedExchangeRate = !!exchangeRate || isUsingDefaultExchangeRate;
  const switchablePushType = normalizeSwitchablePushType(pushType);
  const canSwitchPushType = !!barkToken && !!wechatReceiver;
  const showsJumpTarget = switchablePushType === 'bark' || switchablePushType === 'wechat';
  const showsBarkToken = switchablePushType === 'bark';
  const showsWechatReceiver = switchablePushType === 'wechat';
  const showsWechatStyle = switchablePushType === 'wechat';
  const wechatStyleDetails = WECHAT_STYLE_DETAILS[wechatStyle] || WECHAT_STYLE_DETAILS[DEFAULT_WECHAT_STYLE];

  return (
    <div className="settings-container">
      <h1>设置</h1>
      <div className="settings-section">
        <h2>账户设置</h2>
        <div className="settings-item">
          <span className="settings-label">邮箱</span>
          <span className="settings-value">{userEmail}</span>
        </div>
        <div className="settings-item">
          <span className="settings-label">汇率</span>
          <div className="settings-value-group settings-value-group--exchange-rate">
            {isUsingDefaultExchangeRate ? (
              <span className="settings-value exchange-rate-default-value">
                <span className="exchange-rate-default-label">默认值</span>
                <span className="exchange-rate-default-number">{defaultExchangeRate}</span>
              </span>
            ) : (
              <span className={`settings-value ${hasDisplayedExchangeRate ? '' : 'settings-value-muted'}`}>
                {exchangeRate || '未设置'}
              </span>
            )}
            <button
              className="edit-bark-button"
              onClick={handleEditExchangeRateClick}
            >
              编辑
            </button>
          </div>
        </div>
        {showsJumpTarget && (
          <div className="settings-item">
            <span className="settings-label">跳转目标</span>
            <div className="settings-value-group settings-value-group--jump-target">
              <JumpTargetDropdown
                value={jumpTarget}
                options={JUMP_TARGET_OPTIONS}
                onChange={handleJumpTargetChange}
                disabled={isJumpTargetSaving}
              />
              {isJumpTargetSaving && (
                <span className="settings-inline-status">保存中...</span>
              )}
            </div>
          </div>
        )}
        <div className="settings-item">
          <div className="settings-label-group">
            <span className="settings-label">推送方式</span>
            {canSwitchPushType && (
              <AutoSaveStatus isSaving={isPushTypeSaving} />
            )}
          </div>
          {canSwitchPushType ? (
            <div className="settings-value-group settings-value-group--push-type">
              <PushTypeSwitch
                value={switchablePushType}
                options={SWITCHABLE_PUSH_TYPES}
                onChange={handlePushTypeChange}
                disabled={isPushTypeSaving}
              />
            </div>
          ) : (
            <span className={`settings-value ${pushType ? '' : 'settings-value-muted'}`}>
              {formatPushType(pushType)}
            </span>
          )}
        </div>
        {showsBarkToken && (
          <div className="settings-item settings-item-stacked">
            <div className="settings-item-header">
              <span className="settings-label">Bark Token</span>
              <button
                className="edit-bark-button"
                onClick={handleEditClick}
              >
                编辑
              </button>
            </div>
            <span
              className="bark-token-display"
            >
              {barkToken || '未设置'}
            </span>
          </div>
        )}
        {showsWechatReceiver && (
          <div className="settings-item">
            <span className="settings-label">企微</span>
            <span className={`settings-value ${wechatReceiver ? '' : 'settings-value-muted'}`}>
              {wechatReceiver || '暂未开放，敬请期待'}
            </span>
          </div>
        )}
        {showsWechatStyle && (
          <div className="settings-item">
            <div className="settings-label-group">
              <span className="settings-label">推送样式</span>
              <AutoSaveStatus isSaving={isWechatStyleSaving} />
            </div>
            <div className="settings-value-group settings-value-group--wechat-style">
              <div className="settings-wechat-style-panel">
                <PushTypeSwitch
                  value={wechatStyle}
                  options={WECHAT_STYLE_OPTIONS}
                  onChange={handleWechatStyleChange}
                  disabled={isWechatStyleSaving}
                  ariaLabel="推送样式"
                />
                <p className="settings-wechat-style-description">
                  {wechatStyleDetails.description}
                </p>
                <button
                  type="button"
                  className="settings-sample-button"
                  onClick={handleOpenWechatStyleSample}
                >
                  查看样例
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <button className="logout-button" onClick={handleLogoutClick}>
        退出登录
      </button>

      {isModalOpen && (
        <SettingsModal
          title="编辑 Bark Token"
          titleId="bark-token-modal-title"
          onClose={() => setIsModalOpen(false)}
          footer={(
            <>
              <button className="modal-button modal-cancel" onClick={() => setIsModalOpen(false)}>
                取消
              </button>
              <button className="modal-button modal-save" onClick={handleSaveBarkToken}>
                保存
              </button>
            </>
          )}
        >
          <textarea
            className="modal-textarea"
            value={tempBarkToken}
            onChange={(e) => setTempBarkToken(e.target.value)}
            placeholder="请输入Bark Token"
            rows={3}
          />
        </SettingsModal>
      )}

      {isWechatStyleSampleOpen && (
        <SettingsModal
          title={`${wechatStyleDetails.label}样例`}
          titleId="wechat-style-sample-title"
          className="wechat-style-sample-modal"
          bodyClassName="wechat-style-sample-body"
          onClose={() => setIsWechatStyleSampleOpen(false)}
        >
          <p className="wechat-style-sample-description">
            {wechatStyleDetails.description}
          </p>
          <div className="wechat-style-sample-list">
            {wechatStyleDetails.samples.map((sampleUrl, index) => {
              const imageStatus = wechatStyleSampleImageStatus[sampleUrl] || 'loading';
              const isImageLoading = imageStatus === 'loading';
              const isImageError = imageStatus === 'error';

              return (
                <figure className="wechat-style-sample-frame" key={sampleUrl}>
                  {wechatStyleDetails.samples.length > 1 && (
                    <figcaption className="wechat-style-sample-caption">
                      示例 {index + 1}
                    </figcaption>
                  )}
                  <div
                    className={`wechat-style-sample-preview wechat-style-sample-preview--${imageStatus}`}
                    data-image-status={imageStatus}
                  >
                    {(isImageLoading || isImageError) && (
                      <div className="wechat-style-sample-placeholder" aria-live="polite">
                        <span className="wechat-style-sample-placeholder-mark" aria-hidden="true" />
                        <span>
                          {isImageError ? '图片加载失败，请稍后重试' : '图片加载中...'}
                        </span>
                      </div>
                    )}
                    <img
                      className={`wechat-style-sample-image wechat-style-sample-image--${imageStatus}`}
                      src={sampleUrl}
                      alt={`${wechatStyleDetails.label}示例 ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                      onLoad={() => updateWechatStyleSampleImageStatus(sampleUrl, 'loaded')}
                      onError={() => updateWechatStyleSampleImageStatus(sampleUrl, 'error')}
                    />
                  </div>
                </figure>
              );
            })}
          </div>
        </SettingsModal>
      )}

      {isExchangeRateModalOpen && (
        <SettingsModal
          title="编辑汇率"
          titleId="exchange-rate-modal-title"
          onClose={() => setIsExchangeRateModalOpen(false)}
          footer={(
            <>
              <button className="modal-button modal-cancel" onClick={() => setIsExchangeRateModalOpen(false)}>
                取消
              </button>
              <button className="modal-button modal-save" onClick={handleSaveExchangeRate}>
                保存
              </button>
            </>
          )}
        >
          <label className="modal-label" htmlFor="exchange-rate-input">汇率</label>
          <input
            id="exchange-rate-input"
            className="modal-input"
            value={tempExchangeRate}
            onChange={(e) => setTempExchangeRate(e.target.value)}
            inputMode="decimal"
            placeholder={defaultExchangeRate || '0.0475'}
          />
        </SettingsModal>
      )}
    </div>
  );
}

export default Settings;
